const getStorageKey = (targetType) =>
  `active_saved_filter__${targetType}`;

/**
 * Утилиты для работы с фильтрами и URL
 */

/**
 * Преобразует объект фильтров в параметры для API.
 *
 * Поля типа checkbox с queryMapping обрабатываются особым образом:
 * вместо передачи значения самого поля (true/false) в параметры запроса
 * подставляются пары из queryMapping.checkedValue когда чекбокс отмечен.
 *
 * Пример конфига поля:
 *   {
 *     "type": "checkbox",
 *     "value": "bank_statement_only",
 *     "queryMapping": {
 *       "checkedValue": { "source_type": "bank_statement" }
 *     }
 *   }
 * При отмеченном чекбоксе добавит q[source_type]=bank_statement.
 * При снятом - ничего не добавит.
 */
export const filtersToApiParams = (filters, searchFields = []) => {
  const params = {};

  // Строим карту queryMapping по value для быстрого доступа
  const queryMappingMap = {};
  searchFields.forEach((field) => {
    if (field.type === "checkbox" && field.queryMapping) {
      queryMappingMap[field.value] = field.queryMapping;
    }
  });

  if (
    filters.searchValues &&
    Object.keys(filters.searchValues).length > 0
  ) {
    Object.entries(filters.searchValues).forEach(([field, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        value === false
      ) {
        return;
      }

      // Если у поля есть queryMapping, то подставляем маппинг вместо значения
      if (queryMappingMap[field]?.checkedValue) {
        Object.entries(queryMappingMap[field].checkedValue).forEach(
          ([apiKey, apiValue]) => {
            params[`q[${apiKey}]`] = apiValue;
          }
        );
        return;
      }

      params[`q[${field}]`] = value;
    });
  }

  if (filters.showInactive === false) {
    params["q[enabled]"] = "true";
  }

  return params;
};

/**
 * Преобразует query параметры URL в объект фильтров
 */
export const urlQueryToFilters = (
  query,
  searchFields,
  defaultShowInactive = null
) => {
  const filters = {
    searchValues: {},
    showInactive: defaultShowInactive,
  };

  // Обрабатываем searchValues из query параметров
  searchFields.forEach((field) => {
    if (field.type === "checkbox" && field.queryMapping?.checkedValue) {
      filters.searchValues[field.value] = Object.entries(
        field.queryMapping.checkedValue
      ).every(([apiKey, apiValue]) => query[`q[${apiKey}]`] === apiValue);
      return;
    }

    const queryKey = `q[${field.value}]`;
    if (query[queryKey] !== undefined && query[queryKey] !== "") {
      // Чекбоксы восстанавливаем как boolean, а не строку
      if (field.type === "checkbox") {
        filters.searchValues[field.value] =
          query[queryKey] === "true";
      } else {
        filters.searchValues[field.value] = query[queryKey];
      }
    }
  });

  // Обрабатываем showInactive
  if (query.showInactive === "true") {
    filters.showInactive = true;
  }

  return filters;
};

/**
 * Преобразует объект фильтров в query параметры URL
 */
export const filtersToUrlQuery = (
  filters,
  currentPage = 1,
  searchFields = []
) => {
  const query = {};
  const queryMappingMap = {};
  searchFields.forEach((field) => {
    if (field.type === "checkbox" && field.queryMapping) {
      queryMappingMap[field.value] = field.queryMapping;
    }
  });

  // Добавляем searchValues в query
  if (
    filters.searchValues &&
    Object.keys(filters.searchValues).length > 0
  ) {
    Object.entries(filters.searchValues).forEach(([field, value]) => {
      if (
        value !== null &&
        value !== undefined &&
        value !== "" &&
        value !== false
      ) {
        if (queryMappingMap[field]?.checkedValue) {
          Object.entries(queryMappingMap[field].checkedValue).forEach(
            ([apiKey, apiValue]) => {
              query[`q[${apiKey}]`] = apiValue;
            }
          );
          return;
        }

        query[`q[${field}]`] = value;
      }
    });
  }

  // Добавляем showInactive
  if (filters.showInactive) {
    query.showInactive = "true";
  }

  // Добавляем страницу, если она не первая
  if (currentPage > 1) {
    query.page = currentPage.toString();
  }

  return query;
};

/**
 * Обновляет URL с учетом фильтров и страницы
 */
export const updateUrlWithFilters = (router, filters, page = 1) => {
  const query = filtersToUrlQuery(filters, page);

  // Удаляем page из query если это первая страница
  if (page === 1 && query.page) {
    delete query.page;
  }

  router.replace({ query }).catch(() => {});
};

/**
 * Очищает фильтры в URL
 */
export const clearUrlFilters = (router) => {
  router.replace({ query: {} }).catch(() => {});
};

/**
 * Получает начальные фильтры из URL или использует значения по умолчанию
 */
export const getInitialFilters = (
  route,
  searchFields,
  defaultFilters = {}
) => {
  if (Object.keys(route.query).length === 0) {
    return defaultFilters;
  }
  return urlQueryToFilters(
    route.query,
    searchFields,
    defaultFilters.showInactive
  );
};

/**
 * Получает текущую страницу из URL
 */
export const getCurrentPageFromUrl = (route) => {
  const page = parseInt(route.query.page);
  return isNaN(page) || page < 1 ? 1 : page;
};

/**
 * Сохраняет id активного сохранённого фильтра в localStorage
 * @param {string} targetType - Тип модели (например, "Order", "Counterparty")
 * @param {number} filterId - ID сохранённого фильтра
 */
export const saveActiveFilterToStorage = (targetType, filterId) => {
  localStorage.setItem(getStorageKey(targetType), String(filterId));
};

/**
 * Удаляет запись об активном сохранённом фильтре из localStorage
 * @param {string} targetType - Тип модели (например, "Order", "Counterparty")
 */
export const clearStoredActiveFilter = (targetType) => {
  localStorage.removeItem(getStorageKey(targetType));
};

/**
 * Возвращает id активного сохранённого фильтра из localStorage
 * @param {string} targetType - Тип модели (например, "Order", "Counterparty")
 * @returns {number|null} - ID фильтра или null если не найден
 */
export const getStoredActiveFilterId = (targetType) => {
  const raw = localStorage.getItem(getStorageKey(targetType));
  return raw ? Number(raw) : null;
};
